import { useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Plus,
  Trash2,
} from 'lucide-react';
import type { Deck } from '@allemnii-pulse/shared';
import { cn } from '../../../lib/utils.js';
import { SlideView } from '../SlideView.js';
import { ToolButton } from './controls.js';

const THUMB_WIDTH = 168;

/** Thumbnails of every slide: select, drag to reorder, and add/duplicate/delete/move. A column
 * beside the canvas on wider screens, a horizontal strip above it on a phone. */
export function SlideRail({
  deck,
  imageUrls,
  current,
  onSelect,
  onAdd,
  onDuplicate,
  onDelete,
  onMove,
}: {
  deck: Deck;
  imageUrls: Record<string, string>;
  current: number;
  onSelect: (index: number) => void;
  onAdd: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /** Moves slide `from` to position `to`. */
  onMove: (from: number, to: number) => void;
}) {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const count = deck.slides.length;

  return (
    <aside className="flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-surface md:w-[196px]">
      <div className="flex items-center justify-between border-b border-border px-1.5 py-1">
        <ToolButton label="Add slide" onClick={onAdd}>
          <Plus />
        </ToolButton>
        <ToolButton label="Duplicate slide" onClick={onDuplicate}>
          <Copy />
        </ToolButton>
        <ToolButton
          label="Move slide up"
          disabled={current === 0}
          onClick={() => onMove(current, current - 1)}
        >
          {/* Arrows follow the strip's direction: sideways on a phone, up/down in the column. */}
          <ChevronLeft className="md:hidden" />
          <ChevronUp className="hidden md:block" />
        </ToolButton>
        <ToolButton
          label="Move slide down"
          disabled={current === count - 1}
          onClick={() => onMove(current, current + 1)}
        >
          <ChevronRight className="md:hidden" />
          <ChevronDown className="hidden md:block" />
        </ToolButton>
        <ToolButton label="Delete slide" disabled={count <= 1} onClick={onDelete}>
          <Trash2 />
        </ToolButton>
      </div>
      <ol className="flex gap-2 overflow-auto p-2 md:flex-1 md:flex-col">
        {deck.slides.map((slide, i) => (
          <li
            key={slide.id}
            draggable
            onDragStart={(e) => {
              setDragFrom(i);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => {
              if (dragFrom === null) return;
              e.preventDefault();
              setDropAt(i);
            }}
            onDragEnd={() => {
              setDragFrom(null);
              setDropAt(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragFrom !== null && dragFrom !== i) onMove(dragFrom, i);
              setDragFrom(null);
              setDropAt(null);
            }}
            className={cn(
              'flex shrink-0 gap-1.5 rounded-md',
              dropAt === i && dragFrom !== null && dragFrom !== i && 'ring-2 ring-accent/50',
            )}
          >
            <span className="w-4 shrink-0 pt-1 text-right font-mono text-[11px] text-muted">
              {i + 1}
            </span>
            <button
              type="button"
              onClick={() => onSelect(i)}
              aria-label={`Slide ${i + 1}`}
              aria-current={i === current}
              className={cn(
                'overflow-hidden rounded border-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-focus',
                i === current ? 'border-accent' : 'border-transparent hover:border-border',
              )}
            >
              <SlideView
                slide={slide}
                design={deck.design}
                imageUrls={imageUrls}
                width={THUMB_WIDTH - 24}
                slideNumber={i + 1}
                className="pointer-events-none"
              />
            </button>
          </li>
        ))}
      </ol>
    </aside>
  );
}
